import { getCurrentInstance, onBeforeUnmount, shallowRef } from 'vue';

import { isLinkSelected } from '@/composables/useNavLinkUtils';

interface RouterCompat {
  afterEach: (guard: () => void) => () => void;
  resolve: (url: string) => unknown;
}

interface UseSpaNavLinkReturn {
  toSpaPath: (url: string) => string | null;
  isLinkActive: (url: string) => boolean;
}

export function useSpaNavLink(): UseSpaNavLinkReturn {
  // getCurrentInstance works with both Vue Router 3 (proxy.$router) and Vue Router 4
  const $router = getCurrentInstance()?.proxy?.$router as RouterCompat | undefined;

  const currentPath = shallowRef(window.location.pathname + window.location.search);

  if ($router) {
    const unsubscribe = $router.afterEach(() => {
      currentPath.value = window.location.pathname + window.location.search;
    });
    onBeforeUnmount(() => { unsubscribe(); });
  }

  function toSpaPath(url: string): string | null {
    if (!$router) return null;
    const resolved = $router.resolve(url);
    // Vue Router 3 returns { route: { matched } }, Vue Router 4 returns { matched } directly
    const matched = Array.isArray((resolved as { matched?: unknown }).matched)
      ? (resolved as { matched: unknown[] }).matched
      : ((resolved as { route?: { matched?: unknown[] } }).route?.matched ?? []);
    return matched.length > 0 ? url : null;
  }

  function isLinkActive(url: string): boolean {
    return isLinkSelected(url, currentPath.value);
  }

  return { toSpaPath, isLinkActive };
}
